/**
 * MESSAGE INBOX — Smart Inbox come cockpit a tre colonne.
 *
 * Layout (concept "Smart Inbox · Cockpit"): barra comandi in alto, poi coda a
 * sinistra, workspace al centro, richiesta pinnata a destra. La PAGINA non
 * scorre — scorrono le colonne — cosi' coda e richiesta restano ferme mentre si
 * legge il centro.
 *
 * Coda: server-paged via useServerPagedSearch. L'input di ricerca alimenta
 * `search` (debounced 300ms), lo scroll della colonna triggera load-more.
 * Ogni riga porta l'anello di completezza (vedi {@link completenessOf}).
 *
 * La coda si rilegge in due modi, entrambi passanti per `handleRefresh`: il
 * pulsante accanto alla ricerca, e il rientro dell'utente sulla pagina (tab o
 * finestra) se nel frattempo sono passati almeno STALE_AFTER_MS. Niente timer:
 * un'inbox lasciata aperta in secondo piano non interroga il server.
 *
 * Tab label ('all' | 'to_process' | 'in_progress' | 'completed' |
 * 'ignored' | 'error') filtrano sul campo server-driven `Message.label`
 * (enum MessageLabel: new, to_process, in_progress, completed, ignored,
 * error): il tab pilota la query server (param label) e il filtro
 * client-side sul chunk gia' caricato.
 *
 * Centro: le due card "Cosa sappiamo" / "Cosa manca" — le stesse MessageInfo di
 * prima, editabili col doppio click — e sotto l'email originale, collassata:
 * nel cockpit il testo grezzo e' la fonte da consultare, non il contenuto
 * primario.
 *
 * Destra: la richiesta selezionata con l'anello grande, la CTA (prima azione
 * contribuita dai plugin, tipicamente "Crea Preventivo") e le azioni core.
 *
 * `selected` e' state locale al componente (riceve direttamente l'oggetto
 * dall'onClick), cosi' resta visibile anche se l'utente cambia query/scorre
 * oltre. aiAnalysis continua a vivere in Redux (popolata da `analyzeMessage`
 * placeholder hardcoded).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../components/useToast';

type Translator = ReturnType<typeof useTranslation>['t'];

import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatFileSize, formatRelativeDate, useServerPagedSearch, useScrollAutoFill } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { analyzeMessage, updateMessage, updateMessageInfo } from './messageThunks';
import { selectMessage } from './messageSlice';
import { messageApi } from './messageApi';
import { MESSAGE_LABELS, type Message, type MessageDirection, type MessageLabel, type AiAnalysis } from './messageTypes';
import {
  groupsBySection,
  extractorShortName,
  getLocalizedLabel,
  formatLocalizedValue,
  BUSINESS_EXISTS_LABEL,
  GMAP_LABEL,
  type MessageInfo,
  type ExtractorGroup,
} from './messageInfoTypes';
import { CompletenessRing } from '../../components/CompletenessRing';
import { SandboxedHtmlFrame } from '../../components/SandboxedHtmlFrame';
import { useAttachmentDownload } from './useAttachmentDownload';
import {
  SearchGlyph,
  RefreshGlyph,
  ChevronDownGlyph,
  AlertGlyph,
  MailGlyph,
  CheckSquareGlyph,
  SettingsGlyph,
  CloseGlyph,
  InfoGlyph,
} from '../../components/icons';
import { useResolvedBusiness } from '../business/useResolvedBusiness';
import { RequestMissingInfoDialog } from './RequestMissingInfoDialog';
import { SystemInfoDialog } from './SystemInfoDialog';
import { EditableField, type EditableValue } from '../../components/EditableField';
import { useActionContributors } from '../../plugin/useActionContributors';
import { useIsCoreItemEnabled } from '../../plugin/useCoreItems';
import { useSidebarContributors } from '../../plugin/useSidebarContributors';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import type { AppDispatch } from '../../store/store';

type TabKey = 'all' | 'to_process' | 'in_progress' | 'completed' | 'ignored' | 'error';

const PAGE_SIZE = 20;
const SCROLL_THRESHOLD_PX = 80;
/**
 * Da quanto la coda deve essere ferma perché il rientro sulla pagina la ricarichi.
 *
 * Senza soglia ogni alt-tab rileggerebbe la lista, e siccome la rilettura riparte da pagina 1
 * (vedi resetOn), uscire un attimo per copiare un indirizzo costerebbe lo scroll fatto fin lì.
 * Trenta secondi separano le due cose che accadono davvero: la sbirciata altrove, che non deve
 * toccare niente, e il ritorno dopo una pausa, dove i dati vecchi sono il problema vero.
 */
const STALE_AFTER_MS = 30_000;

/**
 * Colore MUI del descriptor di label -> variante del tag nella coda. La mappa
 * esiste perche' MESSAGE_LABELS parla la lingua di MUI ('warning', 'success')
 * mentre il design system usa i nomi dei semantici ('warn', 'ok').
 */
const LABEL_TONE: Record<string, string> = {
  default: 'neutral',
  warning: 'warn',
  info: 'info',
  success: 'ok',
  error: 'crit',
};

/**
 * Completezza di un messaggio: quante delle informazioni attese sono gia' state
 * estratte. Non e' un dato del backend — si ricava dalle stesse liste che
 * alimentano "Cosa sappiamo" e "Cosa manca", cosi' l'anello non puo' raccontare
 * qualcosa di diverso da quello che si legge sotto.
 *
 * Senza extractor (total 0) la percentuale e' 0 e il chiamante mostra l'anello
 * vuoto: e' l'unica risposta onesta, "non lo sappiamo" non "e' completo".
 */
function completenessOf(message: Message): { done: number; total: number; pct: number } {
  const count = (section: 'populated' | 'missing') =>
    groupsBySection(message.extractorInfos ?? [], section)
      .reduce((n, g) => n + g.infos.length, 0);
  const done = count('populated');
  const total = done + count('missing');
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) };
}

export default function MessageInbox() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();
  const { aiAnalysis } = useAppSelector((state) => state.messages);
  const surveillanceEnabled = useIsCoreItemEnabled('surveillance');

  const [activeTab, setActiveTab] = useState<TabKey>('all');
  // Direzione = segmento di path server-side (IN | OUT, niente "all"): cambiarla rifà la fetch
  // (vedi resetOn). Default IN (posta in arrivo). Il combo stato sotto è visibile solo per IN;
  // per OUT il filtro è implicitamente "all" ma `activeTab` resta preservato per il ritorno a IN.
  const [direction, setDirection] = useState<MessageDirection>('IN');

  // Totale messaggi per direzione mostrato fra parentesi sui tab Ricevuti/Inviati. Conta i soli
  // stati aperti (Da processare, In corso, In errore): è quello che il backend restituisce quando
  // `label` è omesso, indipendente dal filtro stato e dalla ricerca. null finché non caricato.
  const [directionCounts, setDirectionCounts] =
    useState<Record<MessageDirection, number> | null>(null);

  // Filtro effettivo applicato a query + filter client-side. Per OUT è SEMPRE 'all' (il combo
  // stato è solo IN-specific). Per IN segue la scelta utente. Pattern: l'UI state (activeTab)
  // sopravvive ai cambi di direction, ma viene applicato solo quando direction === 'IN'.
  const effectiveFilter: TabKey = direction === 'OUT' ? 'all' : activeTab;
  const [selected, setSelected] = useState<Message | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Override locali dei Message modificati dall'utente in questa sessione:
  // useServerPagedSearch mantiene `items` fuori da Redux e non si rinfresca
  // dopo le PATCH, quindi se l'utente cambia messaggio e torna, senza
  // overrides vedrebbe lo snapshot stale della fetch iniziale.
  // Chiave = messageId, valore = patch shallow applicato a Message.
  // Unico canale per qualsiasi campo modificabile (label, infos, ...) —
  // l'helper patchMessage aggiorna questo state e `selected` insieme.
  const [messageOverrides, setMessageOverrides] = useState<Record<number, Partial<Message>>>({});

  // Token di reload della lista: entra in resetOn, quindi incrementarlo forza un refetch anche
  // quando groupId/direction/filtro restano identici (useServerPagedSearch non espone un refetch
  // esplicito). Serve su OUT dopo "Ignora Richiesta": lì non c'è un combo stato da spostare, e
  // senza refetch il messaggio ignorato resterebbe nella lista.
  const [reloadToken, setReloadToken] = useState(0);

  // fetchPage stabile: la dipendenza da groupId e' passata via resetOn
  // (l'hook non va re-runnato a ogni render del parent).
  // Filtro stato server-side: 'all' -> nessun label, e il backend risponde con i soli stati
  // aperti; gli altri TabKey coincidono 1:1 con i valori MessageLabel. Cambiando tab scatta
  // resetOn -> refetch paginato per quel solo stato.
  const fetchPage = useCallback(
    (query: string, page: number, pageSize: number) =>
      messageApi
        .getAll(groupId, {
          direction,
          label: effectiveFilter === 'all' ? undefined : effectiveFilter,
          search: query || undefined,
          page,
          pageSize,
          sort: 'receiptDate:desc',
        })
        .then((p) => ({ items: p.items, totalCount: p.totalCount })),
    [groupId, direction, effectiveFilter],
  );

  const { items, totalCount, loading, query, setQuery, loadMore, hasMore } = useServerPagedSearch<Message>({
    fetchPage,
    pageSize: PAGE_SIZE,
    resetOn: [groupId, direction, effectiveFilter, reloadToken],
    enabled: groupId > 0,
  });

  // Conteggi mostrati fra parentesi sui tab direzione (IN/OUT), con due getAll leggere
  // (pageSize 1, solo totalCount) in parallelo. Non variano al cambio di filtro/ricerca, quindi
  // si ricaricano solo al cambio di groupId e a ogni refresh esplicito: dopo un "Aggiorna" un
  // numero fermo sul valore di dieci minuti prima si legge come dato buono, ed è il modo più
  // silenzioso di mentire.
  useEffect(() => {
    if (groupId <= 0) {
      setDirectionCounts(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      messageApi.getAll(groupId, { direction: 'IN', pageSize: 1 }),
      messageApi.getAll(groupId, { direction: 'OUT', pageSize: 1 }),
    ])
      .then(([inPage, outPage]) => {
        if (cancelled) return;
        setDirectionCounts({ IN: inPage.totalCount, OUT: outPage.totalCount });
      })
      .catch(() => {
        // Silent: il conteggio è un di più, l'inbox resta usabile senza.
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, reloadToken]);

  // Quando la coda è stata letta dal server l'ultima volta. Ref e non state di proposito: la
  // leggono solo i listener, e farne uno state ri-renderebbe la pagina a ogni fetch senza che
  // cambi un pixel.
  const lastLoadedAtRef = useRef(Date.now());
  // Refresh esplicito in corso, distinto da `loading`: quest'ultimo è vero anche durante un
  // load-more, e legarci il pulsante lo faceva girare mentre l'utente si limitava a scorrere.
  const [refreshing, setRefreshing] = useState(false);
  // Annuncio per screen reader: la coda si riscrive senza che nulla lo dica a chi non la vede.
  const [refreshAnnounce, setRefreshAnnounce] = useState('');
  // Id del messaggio aperto a destra, leggibile fuori dal ciclo di render: handleRefresh deve
  // restare stabile (ce lo tengono i listener di rientro) e quindi non può dipendere da `selected`.
  const selectedIdRef = useRef<number | null>(null);
  useEffect(() => {
    selectedIdRef.current = selected?.messageId ?? null;
  }, [selected]);

  // Fine di un ciclo di fetch, riconosciuta sul fronte di discesa di `loading` e non sul suo
  // valore: al mount `loading` è già false senza che si sia letto niente, e la marca temporale
  // deve dire "letto", non "fermo".
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (wasLoadingRef.current && !loading) {
      // Vale ogni fetch, non solo i refresh: anche un cambio di filtro o un load-more ha appena
      // parlato col server, e dopo non c'è nulla da rinfrescare.
      lastLoadedAtRef.current = Date.now();
      if (refreshing) {
        setRefreshing(false);
        setRefreshAnnounce(t('message.refreshed', { count: totalCount }));
      }
    }
    wasLoadingRef.current = loading;
  }, [loading, refreshing, totalCount, t]);

  // Rilettura della coda dal server. Passa da reloadToken invece che da una fetch diretta
  // così la ricarica prende la stessa strada di un cambio di filtro: le guardie anti-race
  // dell'hook valgono anche qui, senza una seconda implementazione da tenere allineata.
  //
  // Gli override locali si buttano: contengono patch già salvate sul server, quindi dopo la
  // rilettura sono nel migliore dei casi ridondanti, e nel peggiore coprono con lo stato di
  // questa sessione una modifica fatta nel frattempo da qualcun altro — senza alcun errore.
  //
  // Il messaggio aperto a destra va riletto a parte: `selected` è uno state indipendente da
  // `items`, quindi senza questa fetch la coda mostrerebbe il dato fresco e il pannello lo
  // snapshot del momento del click — due versioni della stessa richiesta nella stessa schermata.
  const handleRefresh = useCallback(() => {
    // La marca si sposta qui, alla richiesta, non all'arrivo della risposta: serve a impedire
    // che due eventi di rientro quasi simultanei lancino due riletture (vedi sotto).
    lastLoadedAtRef.current = Date.now();
    setRefreshing(true);
    // Azzerato perché l'annuncio successivo potrebbe essere identico al precedente: senza un
    // passaggio da stringa vuota il DOM non cambia e aria-live non dice nulla.
    setRefreshAnnounce('');
    setMessageOverrides({});
    setReloadToken((t) => t + 1);

    const messageId = selectedIdRef.current;
    if (messageId === null) return;
    messageApi
      .getById(messageId)
      // Il guard sull'id serve perché l'utente può cambiare selezione mentre la fetch è in volo:
      // senza, la risposta vecchia rimpiazzerebbe il messaggio appena aperto.
      .then((fresh) => setSelected((prev) => (prev?.messageId === fresh.messageId ? fresh : prev)))
      .catch(() => {
        // Silent: il dettaglio resta quello che c'era. La coda si è comunque aggiornata, e un
        // toast qui direbbe "errore" per una schermata che all'utente appare a posto.
      });
  }, []);

  // Aggiornamento automatico: nessun timer, la coda si rilegge quando l'utente torna a
  // guardarla. Chi lascia l'inbox aperta in secondo piano non genera traffico, e chi ci rientra
  // non legge una lista di mezz'ora prima.
  //
  // Servono entrambi gli eventi: `visibilitychange` copre il cambio di tab e la minimizzazione,
  // `focus` il passaggio a un'altra applicazione — lì la tab resta "visible" e visibilitychange
  // non scatta. Quando scattano insieme il secondo non fa nulla, perché handleRefresh ha già
  // spostato la marca temporale dentro la soglia.
  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadedAtRef.current < STALE_AFTER_MS) return;
      handleRefresh();
    };
    document.addEventListener('visibilitychange', refreshIfStale);
    window.addEventListener('focus', refreshIfStale);
    return () => {
      document.removeEventListener('visibilitychange', refreshIfStale);
      window.removeEventListener('focus', refreshIfStale);
    };
  }, [handleRefresh]);

  const listRef = useRef<HTMLDivElement | null>(null);
  useScrollAutoFill(listRef, { hasMore, loading, loadMore, deps: [items] });

  // Deep-link: /inbox?messageId=X pre-seleziona il messaggio (es. quando
  // si torna qui dopo aver creato un preventivo dal pulsante "Crea
  // Preventivo"). Se il messaggio e' gia' in `items` lo usiamo diretto,
  // altrimenti cade sulla fetch by-id via messageApi. La query param
  // viene rimossa dopo il pickup per evitare che un re-render / reload
  // re-triggeri la selezione sovrascrivendo scelte successive dell'utente.
  useEffect(() => {
    const raw = searchParams.get('messageId');
    if (!raw) return;
    if (selected && selected.messageId === Number(raw)) return;
    const mid = Number(raw);
    if (!Number.isFinite(mid)) return;
    const fromList = items.find((m) => m.messageId === mid);
    if (fromList) {
      handleSelect(fromList);
      setSearchParams({}, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const msg = await messageApi.getById(mid);
        if (cancelled) return;
        handleSelect(msg);
        setSearchParams({}, { replace: true });
      } catch {
        // Silent: se il fetch fallisce l'utente vede l'inbox senza
        // selezione; niente toast perche' il link potrebbe essere
        // stale (messaggio cancellato nel frattempo).
      }
    })();
    return () => { cancelled = true; };
    // Deps intenzionalmente ridotte: handleSelect cambia ad ogni render
    // ma ha gia' il guard anti-reselezione; setSearchParams e' stable
    // (React Router); `selected` si aggiorna dentro handleSelect e non
    // va in dep per evitare loop (dopo il pickup l'effect non deve piu'
    // riscattare finche' non cambiano query param o items).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items]);

  const TABS = useMemo<{ key: TabKey; label: string; filter?: MessageLabel }[]>(
    () => [
      { key: 'all', label: t('message.tabs.all') },
      { key: 'to_process', label: t('message.tabs.toProcess'), filter: 'to_process' },
      { key: 'in_progress', label: t('message.tabs.inProgress'), filter: 'in_progress' },
      { key: 'completed', label: t('message.tabs.completed'), filter: 'completed' },
      { key: 'ignored', label: t('message.tabs.ignored'), filter: 'ignored' },
      { key: 'error', label: t('message.tabs.error'), filter: 'error' },
    ],
    [t],
  );

  // Applica gli override locali alla lista prima di filtrare/visualizzare:
  // la Chip di label e il tab di appartenenza riflettono subito la patch
  // senza attendere un refetch (useServerPagedSearch non espone setter).
  const itemsWithOverrides = useMemo(
    () => items.map((m) => {
      const ov = messageOverrides[m.messageId];
      return ov ? { ...m, ...ov } : m;
    }),
    [items, messageOverrides],
  );
  const tabFilter = TABS.find((tb) => tb.key === effectiveFilter)?.filter;
  const visible = tabFilter ? itemsWithOverrides.filter((m) => m.label === tabFilter) : itemsWithOverrides;

  // Helper unico per applicare un patch shallow a un Message: aggiorna
  // `messageOverrides` (persistenza cross-selezione) e `selected` (UI
  // immediata) in un colpo solo. Tutti gli handler di modifica in-place
  // del Message passano da qui.
  const patchMessage = useCallback((messageId: number, patch: Partial<Message>) => {
    setMessageOverrides((prev) => ({
      ...prev,
      [messageId]: { ...prev[messageId], ...patch },
    }));
    setSelected((prev) => (prev && prev.messageId === messageId ? { ...prev, ...patch } : prev));
  }, []);

  function handleSelect(msg: Message) {
    // No-op se l'utente ri-clicca sul messaggio corrente: evita il
    // re-fetch AI placeholder e preserva eventuali patch in corso.
    if (selected?.messageId === msg.messageId) return;
    // Applica eventuali override fatti in questa sessione prima di
    // salvare in `selected`: evita che la prima selezione di un
    // messaggio gia' modificato faccia ri-apparire lo snapshot stale.
    const override = messageOverrides[msg.messageId];
    const patched: Message = override ? { ...msg, ...override } : msg;
    setSelected(patched);
    dispatch(selectMessage(msg.messageId));
    dispatch(analyzeMessage(msg.messageId));
  }

  // "Ignora Richiesta": patch server (label=ignored) + patch locale, poi il messaggio va tolto
  // dalla vista corrente. Su IN il combo si sposta su "Ignorate": il cambio di filtro entra in
  // resetOn e rifà la fetch, quindi la lista si ripopola con gli ignorati, questo compreso. Su
  // OUT non c'è un combo da spostare, quindi il refetch lo forza reloadToken: il backend senza
  // `label` restituisce i soli stati aperti e il messaggio sparisce dalla lista. In entrambi i
  // casi resta selezionato col detail aperto, come già accadeva quando una riga usciva dal
  // filtro corrente.
  const handleIgnore = useCallback(async () => {
    if (!selected) return;
    const result = await dispatch(updateMessage({ messageId: selected.messageId, label: 'ignored' }));
    if (!updateMessage.fulfilled.match(result)) return;
    patchMessage(result.payload.messageId, { label: result.payload.label });
    // Il contatore della direzione corrente cala di 1 senza rileggerlo dal server. Cala anche
    // ignorando un messaggio Completato, che fra gli stati aperti non era contato: è una scelta
    // esplicita, e il numero si riallinea al prossimo ricalcolo (cambio di groupId o refresh).
    setDirectionCounts((prev) => (
      prev ? { ...prev, [direction]: Math.max(0, prev[direction] - 1) } : prev
    ));
    if (direction === 'IN') setActiveTab('ignored');
    else setReloadToken((t) => t + 1);
  }, [selected, direction, dispatch, patchMessage]);

  // Patch granulare di una singola info: ricostruisce il nuovo array
  // `extractorInfos` (le sole info editabili) a partire dalla versione "piu'
  // fresca" disponibile (selected corrente, poi override precedente, poi
  // snapshot da items) e lo salva tramite patchMessage. Le info predefinite
  // (Message.infos) sono read-only e non passano mai di qui.
  const handleInfoUpdated = useCallback((fresh: MessageInfo) => {
    const msgId = fresh.messageId;
    const baseGroups =
      (selected?.messageId === msgId ? selected.extractorInfos : undefined)
      ?? messageOverrides[msgId]?.extractorInfos
      ?? items.find((m) => m.messageId === msgId)?.extractorInfos
      ?? [];
    // extractorInfos e' raggruppato per extractor: sostituisco l'info aggiornata
    // dentro il suo gruppo, lasciando invariati gruppi e ordine.
    const newGroups = baseGroups.map((g) => ({
      ...g,
      infos: g.infos.map((i) => (i.messageInfoId === fresh.messageInfoId ? fresh : i)),
    }));
    patchMessage(msgId, { extractorInfos: newGroups });
  }, [selected, messageOverrides, items, patchMessage]);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX) loadMore();
  }

  return (
    // Cockpit a tre colonne: la pagina non scorre, scorrono le colonne. Cosi'
    // la coda a sinistra e la richiesta a destra restano ferme mentre si legge
    // il centro, che e' il punto del concept.
    <div className="inbox-cockpit">
      {/* BARRA COMANDI — solo l'intestazione della schermata. Ricerca e
          direzione stanno nella colonna sinistra: filtrano quella coda e
          nient'altro, quindi e' li' che devono vivere. */}
      <div className="cmd">
        <div>
          <h1>{t('message.title')}</h1>
          <div className="sub">{t('message.subtitle')}</div>
        </div>
        {/* La Sorveglianza (FILO-58) ha la sua voce di menu: questo è il passaggio diretto dalla
            posta in arrivo. Spenta la voce, sparisce anche il pulsante. */}
        {surveillanceEnabled && (
          <Link to="/surveillance" className="btn-ghost cmd-link">{t('nav.surveillance')}</Link>
        )}
      </div>

      <div className="tri">
        {/* ─── CODA ─── */}
        <div className="queue">
          <div className="q-head">
            {/* Ricerca e aggiornamento sulla stessa riga: il refresh ricarica proprio
                questa coda, quindi sta fra i suoi controlli e non nella barra del titolo. */}
            <div className="q-tools">
              <div className="cmd-search">
                <SearchGlyph />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('message.searchPlaceholder')}
                  aria-label={t('message.searchPlaceholder')}
                />
              </div>
              {/* Disabilitato durante il refresh: un secondo click non anticipa nulla (l'hook
                  scarterebbe comunque la risposta più vecchia) e toglie il solo segnale di
                  attesa che c'è, cioè la rotazione del glifo. Durante un load-more resta invece
                  attivo: lì il refresh è una richiesta legittima, e la sua fetch vince su quella
                  della pagina successiva. */}
              <button
                type="button"
                className={refreshing ? 'q-refresh q-refresh--busy' : 'q-refresh'}
                onClick={handleRefresh}
                disabled={refreshing || groupId <= 0}
                title={t('message.refresh')}
                aria-label={t('message.refresh')}
              >
                <RefreshGlyph />
              </button>
            </div>

            {/* Segmentato al posto del ToggleButtonGroup MUI: due sole voci, e
                il conteggio sta dentro l'etichetta come nel concept. */}
            <div className="seg" role="group" aria-label={t('message.directionLabel')}>
              {(['IN', 'OUT'] as MessageDirection[]).map((d) => (
                <button
                  key={d}
                  type="button"
                  className={direction === d ? 'on' : undefined}
                  aria-pressed={direction === d}
                  onClick={() => setDirection(d)}
                >
                  {d === 'IN' ? t('message.direction.in') : t('message.direction.out')}
                  {directionCounts && ` (${directionCounts[d]})`}
                </button>
              ))}
            </div>

            {/* Filtro stato: solo per posta IN. Per OUT il filtro è implicitamente
                "tutte" perché lo forza effectiveFilter; activeTab resta com'era e
                torna in vigore rientrando su IN.

                Niente etichetta a vista: le voci ("Tutte", "Da processare", …)
                dicono gia' che si sta filtrando per stato. Resta come aria-label
                per chi naviga con lo screen reader. */}
            {direction === 'IN' && (
              <div className="q-sel">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value as TabKey)}
                  aria-label={t('message.tabs.label')}
                >
                  {TABS.map((tab) => (
                    <option key={tab.key} value={tab.key}>{tab.label}</option>
                  ))}
                </select>
                <ChevronDownGlyph />
              </div>
            )}
          </div>
          <div ref={listRef} className="q-scroll" onScroll={handleScroll}>
            {loading && items.length === 0 && <p className="text-muted text-sm">{t('common.loading')}</p>}
            {visible.map((msg) => (
              <QueueItem
                key={msg.messageId}
                message={msg}
                selected={selected?.messageId === msg.messageId}
                onSelect={() => handleSelect(msg)}
              />
            ))}
            {/* Coda vuota: senza una riga qui il pannello resta bianco e si legge come un guasto,
                non come una risposta. Le cause sono TRE e vanno distinte, perché suggeriscono azioni
                diverse — togliere la ricerca, cambiare stato, o non fare nulla perché la casella è
                davvero vuota. Ricerca e filtro sono entrambi server-side (cfr. fetchPage), quindi
                nessuna delle tre è deducibile guardando i soli `items`.
                La ricerca ha la precedenza sul filtro: se sono attivi entrambi, è quella che l'utente
                ha appena digitato, ed è la prima cosa che proverebbe a togliere.
                Su `visible` e non su `items` perché è ciò che si rende: un override locale di label
                può spostare fuori dal filtro corrente l'ultimo messaggio rimasto, e la lista sarebbe
                vuota con `items` ancora pieno. */}
            {!loading && visible.length === 0 && (
              <p className="text-muted text-sm">
                {query.trim()
                  ? t('message.emptySearch')
                  : effectiveFilter === 'all'
                    ? t('message.empty')
                    : t('message.emptyFilter')}
              </p>
            )}
            {loading && items.length > 0 && <p className="text-muted text-sm">{t('common.loading')}</p>}
          </div>
          {/* La coda si riscrive sotto senza che nulla lo dica a chi non la vede. `role="status"`
              (aria-live polite implicito) annuncia l'esito a fine refresh senza interrompere quello
              che lo screen reader sta leggendo. Sempre nel DOM, mai vuoto solo a intermittenza: una
              regione live inserita insieme al suo testo spesso non viene annunciata. */}
          <p className="visually-hidden" role="status">{refreshAnnounce}</p>
        </div>

        {/* ─── WORKSPACE ─── */}
        <div className="wsp">
          {!selected ? (
            <div className="wsp-empty">{t('message.selectMessage')}</div>
          ) : (
            <div className="wsp-inner">
              <MessageCenter message={selected} onInfoUpdated={handleInfoUpdated} />
            </div>
          )}
        </div>

        {/* ─── RICHIESTA PINNATA ─── */}
        <aside className="reqpanel">
          {selected && (
            <MessageSidebar message={selected} aiAnalysis={aiAnalysis} onIgnore={handleIgnore} />
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * Riga della coda: anello di completezza (o pastiglia d'errore), mittente,
 * oggetto come "intento", stato e tempo relativo.
 *
 * È un <button>: la riga è l'unico bersaglio cliccabile, e da <div> non era
 * raggiungibile da tastiera.
 */
function QueueItem({
  message,
  selected,
  onSelect,
}: {
  message: Message;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const { pct } = useMemo(() => completenessOf(message), [message]);
  const descriptor = MESSAGE_LABELS.find((l) => l.value === message.label)
    ?? MESSAGE_LABELS.find((l) => l.value === 'new')!;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected}
      className={`q-item${selected ? ' q-item--sel' : ''}`}
    >
      {/* Su un messaggio in errore l'anello non ha senso: non c'e' stata
          estrazione da misurare. Al suo posto la pastiglia critica. */}
      {message.label === 'error' ? (
        <span className="q-err" aria-hidden="true"><AlertGlyph /></span>
      ) : (
        <CompletenessRing value={pct} size={34} />
      )}
      <span className="q-main">
        <span className="q-from">{message.senderName || message.senderEmailAddress}</span>
        <span className="q-intent">
          <span className="ar" aria-hidden="true">↳</span>
          {message.subject}
        </span>
        <span className="q-meta">
          <span className={`q-tag ${LABEL_TONE[descriptor.color]}`}>{t(descriptor.label)}</span>
          {message.attachments && message.attachments.length > 0 && (
            <AttachFileIcon className="q-attach" fontSize="inherit" titleAccess={t('message.attachments')} />
          )}
          <span className="q-time">{formatRelativeDate(message.receiptDate)}</span>
        </span>
      </span>
    </button>
  );
}

/**
 * Colonna centrale del detail di un messaggio: intestazione (subject,
 * mittente, data), corpo mail, e tutte le sezioni "AI/info" (Riepilogo,
 * Informazioni estratte, Informazioni mancanti, Prossimo passo suggerito).
 */
function MessageCenter({
  message,
  onInfoUpdated,
}: {
  message: Message;
  onInfoUpdated: (fresh: MessageInfo) => void;
}) {
  const { t } = useTranslation();
  const centerContributors = useSlotContributors('Message', 'inbox:center');

  // Scarica l'allegato come Blob e ne forza il salvataggio su disco col nome
  // originale. Il contenuto non è mai nel Message: è una GET dedicata streaming.
  const handleDownloadAttachment = useAttachmentDownload();

  const { done, total } = useMemo(() => completenessOf(message), [message]);

  // Ordine della colonna: riepilogo, informazioni, email originale, note.
  // Dal piu' sintetico al piu' grezzo — si scende solo se serve.
  return (
    <>
      {message.summary && (
        <section className="wsp-summary">
          <div className="know-h">{t('message.aiSummary')}</div>
          <p>{message.summary}</p>
        </section>
      )}

      {/* Le due card affiancate sono il cuore del concept: a sinistra quello che
          il sistema ha capito, a destra quello che gli manca per procedere.
          Stanno in un accordion unico, apribile e richiudibile: sono la parte
          piu' alta della pagina e chiuderle serve a raggiungere il resto. */}
      <details className="orig know-acc" open>
        <summary>
          <CheckSquareGlyph />
          {t('message.infoSection')}
          <span className="om">{t('message.completeness.count', { done, total })}</span>
          <ChevronDownGlyph className="chv" />
        </summary>
        <div className="know">
          <MessageInfoList message={message} onInfoUpdated={onInfoUpdated} />
          <MessageInfoMissing message={message} onInfoUpdated={onInfoUpdated} />
        </div>
      </details>

      {/* L'email originale scende sotto e parte CHIUSA: nel cockpit il testo
          grezzo e' la fonte da consultare, non il contenuto primario — quello
          e' il riassunto strutturato qui sopra. */}
      <details className="orig">
        <summary>
          <MailGlyph />
          {t('message.originalEmail')}
          <span className="om">
            {t('common.from', {
              sender: message.senderName
                ? `${message.senderName} <${message.senderEmailAddress}>`
                : message.senderEmailAddress,
            })} · {formatRelativeDate(message.receiptDate)}
          </span>
          <ChevronDownGlyph className="chv" />
        </summary>

        {message.bodyHtml ? (
          // HTML email non fidato: reso in un iframe isolato, così eventuali <script>/handler
          // nel markup della mail non vengono mai eseguiti nel contesto dell'app. Il
          // contenitore vive in SandboxedHtmlFrame.
          <SandboxedHtmlFrame
            className="orig__body--html"
            title={t('message.bodyHtmlTitle')}
            html={message.bodyHtml}
          />
        ) : (
          <div className="orig__body">{message.body}</div>
        )}

        {message.attachments && message.attachments.length > 0 && (
          <div className="att">
            {message.attachments.map((att) => (
              <button
                key={att.messageAttachmentId}
                type="button"
                title={t('message.attachmentDownload')}
                onClick={() => handleDownloadAttachment(att)}
              >
                <AttachFileIcon fontSize="inherit" />
                <span>{att.fileName}</span>
                <span className="sz">{formatFileSize(att.size)}</span>
                <DownloadIcon fontSize="inherit" />
              </button>
            ))}
          </div>
        )}
      </details>

      {/* Contributi dei plugin in fondo alla colonna centrale: oggi le "Note
          rapide" di Trentino, che il concept colloca proprio qui. */}
      {centerContributors.map((c) => (
        <c.component key={c.key} entityId={message.messageId} />
      ))}
    </>
  );
}

/**
 * Colonna destra del detail di un messaggio (detail-right 300px):
 * pulsanti azione contribuiti dai plugin abilitati per entityType
 * "Message" (es. Crea/Collega Preventivo dal plugin trentino-quotation),
 * pulsanti core fissi (Richiedi info, Ignora), prossimo passo suggerito
 * dall'AI, box note rapide.
 *
 * Le azioni plugin-driven compaiono solo se il plugin corrispondente
 * e' autorizzato dal backend (user.plugins) e non disabilitato via
 * toggle localStorage. Quando il plugin non e' attivo, i pulsanti
 * spariscono senza alcun codice condizionale nel core.
 */
function MessageSidebar({
  message,
  aiAnalysis,
  onIgnore,
}: {
  message: Message;
  aiAnalysis: AiAnalysis | null;
  onIgnore: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pluginActions = useActionContributors('Message');
  const sidebarContributors = useSidebarContributors('Message');
  const ctaContributors = useSlotContributors('Message', 'inbox:cta');
  const [requestMissingOpen, setRequestMissingOpen] = useState(false);
  const [systemInfoOpen, setSystemInfoOpen] = useState(false);
  type MessageAction = (typeof pluginActions)[number];
  // Contributor attivo quando ha un `component`: il core lo monta come
  // overlay (drawer/dialog a carico del plugin) finche' il componente
  // chiama onClose. onClick semplici (es. navigate) non attivano lo slot.
  const [activeContributor, setActiveContributor] = useState<MessageAction | null>(null);
  const ActiveContributorComponent = activeContributor?.component;

  const { done, total, pct } = useMemo(() => completenessOf(message), [message]);
  const missing = total - done;
  const descriptor = MESSAGE_LABELS.find((l) => l.value === message.label)
    ?? MESSAGE_LABELS.find((l) => l.value === 'new')!;
  // La prima azione contribuita e' la CTA piena del pannello (tipicamente "Crea
  // Preventivo"); le altre scendono fra le azioni secondarie. Se nessun plugin
  // e' attivo non c'e' CTA, e il pannello resta con le sole azioni core.
  const [ctaAction, ...otherActions] = pluginActions;
  const runAction = (action: MessageAction) => {
    if (action.component) setActiveContributor(action);
    else action.onClick?.(message, { navigate });
  };

  return (
    <section className="rp-card">
      <div className="rp-eyebrow">
        <span className={`chip sm ${LABEL_TONE[descriptor.color] === 'neutral' ? 'neutral' : LABEL_TONE[descriptor.color]}`}>
          {t(descriptor.label)}
        </span>
      </div>
      <h2 className="rp-subject">{message.subject}</h2>
      <div className="rp-from">
        {message.senderName ? `${message.senderName} <${message.senderEmailAddress}>` : message.senderEmailAddress}
        <br />
        {t('message.received')}: {formatRelativeDate(message.receiptDate)}
      </div>

      {/* Anello grande + didascalia "x di y dati". E' la stessa misura degli
          anelli della coda: una sola fonte, `completenessOf`. */}
      {total > 0 && (
        <div className="rp-ringwrap">
          <CompletenessRing value={pct} size={84} />
          <div className="rp-ringcap">
            <b>{t('message.completeness.count', { done, total })}</b>
            <span>{t('message.completeness.ready', { pct })}</span>
          </div>
        </div>
      )}

      {ctaAction && (
        <button
          type="button"
          className="sa sa--primary rp-cta"
          onClick={() => runAction(ctaAction)}
        >
          {t(ctaAction.labelKey)}
        </button>
      )}

      {/* Azione principale contribuita dai plugin (slot `inbox:cta`): sopra il
          suggerimento dell'AI, che parla proprio di cosa fare prima di
          lanciarla. */}
      {ctaContributors.map((c) => (
        <c.component key={c.key} entityId={message.messageId} />
      ))}

      {/* Suggerimento del concept: con informazioni mancanti conviene chiederle
          prima di procedere. Il link apre lo stesso dialog del pulsante sotto. */}
      {missing > 0 && (
        <div className="rp-hint">
          <AlertGlyph />
          <span>
            {t('message.requestMissing.hint', { count: missing })}{' '}
            <button type="button" className="rp-hint__link" onClick={() => setRequestMissingOpen(true)}>
              {t('message.requestMissing.hintCta')}
            </button>
          </span>
        </div>
      )}

      <div className="rp-actions">
        {otherActions.map((action) => (
          <button
            key={action.key}
            type="button"
            className="qa qa--full"
            onClick={() => runAction(action)}
          >
            {t(action.labelKey)}
          </button>
        ))}
        {/* Click apre RequestMissingInfoDialog: email di reply precompilata
            con elenco MessageInfo vuote. Invio attualmente stubbato
            (console.log + toast); sostituire con call backend reale
            quando disponibile (es. POST /messages/{id}/reply). */}
        <button type="button" className="qa qa--full" onClick={() => setRequestMissingOpen(true)}>
          <InfoGlyph />
          {t('message.requestMissing.trigger')}
        </button>
        {/* Apre la modale read-only con tutte le MessageInfo di sistema
            (Message.infos). Mostrato solo se ce ne sono: con zero info
            la modale sarebbe vuota e il pulsante inutile. */}
        {message.infos.length > 0 && (
          <button type="button" className="qa qa--full" onClick={() => setSystemInfoOpen(true)}>
            <SettingsGlyph />
            {t('message.systemInfo.trigger')}
          </button>
        )}
        <button
          type="button"
          className="qa qa--full qa--danger"
          onClick={onIgnore}
          disabled={message.label === 'ignored'}
        >
          <CloseGlyph />
          {t('message.ignoreRequest')}
        </button>
      </div>
      {/* Sidebar contributors dei plugin abilitati: pezzi di UI
          sempre visibili (es. "Note rapide" dal plugin trentino-quotation)
          che ricevono l'entity corrente. */}
      {sidebarContributors.map((c) => {
        const Component = c.component;
        return (
          <Component
            key={c.key}
            entity={message}
          />
        );
      })}
      {/* Prossimo passo suggerito (AI): in fondo, dopo azioni e contributi. */}
      {aiAnalysis?.status === 'succeeded' && (
        <div className="mt-xl">
          <AiSection title={t('message.suggestedStep')} content={aiAnalysis.suggestedNextStep} />
        </div>
      )}
      {requestMissingOpen && (
        <RequestMissingInfoDialog
          message={message}
          onClose={() => setRequestMissingOpen(false)}
        />
      )}
      {systemInfoOpen && (
        <SystemInfoDialog
          message={message}
          onClose={() => setSystemInfoOpen(false)}
        />
      )}
      {/* Overlay del contributor attivo (drawer/dialog a carico del plugin):
          fuori dal flusso delle azioni, e' un layer non un pulsante. */}
      {ActiveContributorComponent && (
        <ActiveContributorComponent
          entity={message}
          onClose={() => setActiveContributor(null)}
        />
      )}
    </section>
  );
}

function AiSection({ title, content, children }: { title: string; content?: string; children?: React.ReactNode }) {
  return (
    <div className="ai-section">
      <div className="ai-section__title">{title}</div>
      {content && <p className="ai-section__content">{content}</p>}
      {children}
    </div>
  );
}

// NB: `ActionButton` (Button MUI con le classi `sidebar-action--*`) e
// `LabelBadge` (Chip MUI) sono stati rimossi col cockpit: le azioni del
// pannello destro sono ora pillole `.sa`/`.qa` e lo stato nella coda un
// `.q-tag`. Le classi `sidebar-action--*` restano in _components.scss finche'
// non si verifica che nessun altro le usi.

/**
 * Commit inline di una MessageInfo: dispatcha updateMessageInfo con il
 * nuovo value (priority invariata) e propaga il MessageInfo aggiornato
 * al chiamante via `onUpdated` (serve perche' MessageInbox tiene
 * `selected` in local state e non legge da Redux). Pattern standard
 * EditableField: ritorna una Promise che rigetta in caso di errore
 * (EditableField mostra il messaggio e tiene aperto l'edit).
 */
function commitMessageInfoValue(
  dispatch: AppDispatch,
  info: MessageInfo,
  newValue: EditableValue,
  onUpdated: (fresh: MessageInfo) => void,
  toast: ReturnType<typeof useToast>,
  t: Translator,
): Promise<void> {
  return dispatch(
    updateMessageInfo({
      messageId: info.messageId,
      messageInfoId: info.messageInfoId,
      payload: { value: (newValue as string | null) ?? '', priority: info.priority },
    }),
  )
    .unwrap()
    .then((fresh) => {
      onUpdated(fresh);
      toast({ severity: 'success', message: t('common.saved') });
    })
    .catch((err: unknown) => {
      toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      // Re-throw per EditableField: rigetto = mantiene aperta la edit
      // mode mostrando il messaggio inline (oltre al toast).
      throw err;
    });
}

/**
 * Copia negli appunti con feedback a toast. Stesso gesto delle righe di
 * contatto in {@link PersonRelationCard}: qui serve perche' un valore lungo
 * (un link di Maps, un indirizzo completo) in riga viene troncato, e l'unico
 * modo di averlo intero resta prenderlo.
 */
async function copyToClipboard(
  value: string,
  toast: ReturnType<typeof useToast>,
  t: Translator,
) {
  try {
    await navigator.clipboard.writeText(value);
    toast({ severity: 'success', message: t('common.copied') });
  } catch {
    toast({ severity: 'error', message: t('common.copyFailed') });
  }
}

/**
 * Sezione "Informazioni estratte": rende le MessageInfo con value
 * popolato. Ogni value e' editabile con doppio click (stessa UX di
 * PersonDetail/BusinessDetail).
 *
 * Empty state quando tutte le chiavi sono vuote (es. mail di spam):
 * mostra il titolo + placeholder per coerenza visiva con le altre
 * sezioni "AI".
 */
function MessageInfoList({
  message,
  onInfoUpdated,
}: {
  message: Message;
  onInfoUpdated: (fresh: MessageInfo) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const groups = useMemo(
    () => groupsBySection(message.extractorInfos ?? [], 'populated'),
    [message.extractorInfos],
  );
  const count = groups.reduce((n, g) => n + g.infos.length, 0);
  return (
    <section className="know-col">
      <div className="know-h">
        <span className="know-dot ok" />
        {t('message.extractedInfo')}
        <span className="know-n">{count}</span>
      </div>
      {groups.length === 0 ? (
        <p className="know-empty">{t('message.infos.empty')}</p>
      ) : (
        groups.map((g) => (
          <GroupedInfoRows
            key={g.className ?? '_unknown'}
            group={g}
            senderEmailAddress={message.senderEmailAddress}
            onInfoUpdated={onInfoUpdated}
            dispatch={dispatch}
            toast={toast}
          />
        ))
      )}
    </section>
  );
}

/**
 * Valore di `businessExists`, ricalcolato dal vivo invece di leggere quello persistito.
 *
 * Il flag scritto dall'extractor è uno snapshot preso quando il messaggio è arrivato, e non
 * viene più ricalcolato: se l'azienda viene censita dopo, resta "false" per sempre. Era
 * questo a far dire alla smart inbox che un'azienda presente non era a sistema, mentre il
 * pannello "Crea preventivo" — che invece chiedeva dal vivo — la mostrava correttamente
 * collegata (TS-82).
 *
 * Sola lettura, a differenza delle altre info: è una risposta che il sistema deduce, non un
 * dato estratto dall'email. Prima finiva in un EditableField come tutte le altre, quindi si
 * poteva perfino correggere a mano — cioè mentire al sistema invece di aggiornarlo.
 */
function BusinessExistsValue({
  companyName,
  companyDomain,
  senderEmailAddress,
}: {
  companyName?: string;
  companyDomain?: string;
  senderEmailAddress?: string | null;
}) {
  const { t } = useTranslation();
  const { status } = useResolvedBusiness(companyName, companyDomain, senderEmailAddress);

  // idle = nessun dato di contatto su cui rispondere, o lookup fallita: meglio non
  // pronunciarsi che affermare il falso in una delle due direzioni: `null` lascia
  // parlare l'emptyLabel.
  const display =
    status === 'loading' ? '…'
    : status === 'idle' ? null
    : t(`messageInfo.values.${status === 'exists'}`);

  // Passa dall'EditableField in sola lettura invece di uno <span> nudo: il valore
  // non si tocca, ma la scatola dev'essere la stessa delle altre righe (padding e
  // bordo trasparente del view mode), altrimenti questa riga resta disallineata
  // rispetto a tutte le altre del gruppo.
  return (
    <EditableField
      value={display}
      variant={{ kind: 'text' }}
      readOnly
      onCommit={() => Promise.resolve()}
    />
  );
}

/**
 * Sotto-sezione "per extractor" dentro "Informazioni estratte" / "Mancanti".
 * Header h4 col titolo del gruppo localizzato + tabella delle MessageInfo
 * del gruppo. Pattern label-translation + value-formatting via i helper
 * di messageInfoTypes (boolean/enum/numeric-unit). In edit mode l'EditableField
 * usa il value raw; in view mode mostra displayOverride localizzato.
 *
 * Eccezione `businessExists`: vedi {@link BusinessExistsValue}.
 */
function GroupedInfoRows({
  group,
  senderEmailAddress,
  onInfoUpdated,
  dispatch,
  toast,
}: {
  group: ExtractorGroup;
  senderEmailAddress?: string | null;
  onInfoUpdated: (fresh: MessageInfo) => void;
  dispatch: ReturnType<typeof useAppDispatch>;
  toast: ReturnType<typeof useToast>;
}) {
  const { t } = useTranslation();
  const groupTitle = t(`messageInfo.groups.${extractorShortName(group.className)}`);
  const valueOf = (label: string) => group.infos.find((i) => i.label === label)?.value;
  // Niente <Table> MUI: nel concept ogni gruppo e' un sottotitolo in
  // maiuscoletto seguito da righe etichetta-a-sinistra / valore-a-destra, che
  // in una tabella non si otterrebbero senza combattere con le celle.
  return (
    <>
      <div className="know-sub">{groupTitle}</div>
      {group.infos.map((info) => {
        const displayValue = formatLocalizedValue(info, t);
        return (
          <InfoRow
            key={info.messageInfoId}
            label={getLocalizedLabel(info, t)}
            value={info.value}
            expandable={info.label !== GMAP_LABEL && info.label !== BUSINESS_EXISTS_LABEL}
          >
            {info.label === BUSINESS_EXISTS_LABEL ? (
              <BusinessExistsValue
                companyName={valueOf('companyName')}
                companyDomain={valueOf('companyDomain')}
                senderEmailAddress={senderEmailAddress}
              />
            ) : (
              <EditableField
                value={info.value}
                variant={{ kind: 'text' }}
                label={getLocalizedLabel(info, t)}
                displayOverride={displayValue !== info.value ? displayValue : undefined}
                onCommit={(v) => commitMessageInfoValue(dispatch, info, v, onInfoUpdated, toast, t)}
              />
            )}
          {/* Solo sul link di Maps: e' l'unico valore che si legge per
              intero soltanto negli appunti, gli altri stanno in riga o si
              capiscono troncati. Fuori da `.krow-v` perche' quello e' lo
              spazio che si restringe — il pulsante resta della sua misura. */}
          {info.label === GMAP_LABEL && info.value !== '' && (
            <IconButton
              size="small"
              className="krow-copy"
              onClick={() => void copyToClipboard(info.value, toast, t)}
              aria-label={t('common.copy')}
              title={t('common.copy')}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          )}
          </InfoRow>
        );
      })}
    </>
  );
}

/**
 * Riga etichetta/valore di "Cosa sappiamo". Il valore in riga è troncato
 * dall'ellissi dell'EditableField: se `expandable` e il testo è davvero
 * tagliato compare un chevron che apre la riga a mo' di accordion, e da
 * aperta il valore va a capo per intero. Il chevron nasce dalla misura, non
 * dalla lunghezza della stringa, perché la larghezza della colonna cambia.
 *
 * Il primo figlio è il valore (va in `.krow-v`, lo spazio che si restringe),
 * il secondo sta in fondo alla riga alla sua misura (il copia del link di Maps).
 */
function InfoRow({
  label,
  value,
  expandable,
  children,
}: {
  label: string;
  value: string;
  expandable: boolean;
  children: [React.ReactNode, React.ReactNode];
}) {
  const { t } = useTranslation();
  const valueRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [truncated, setTruncated] = useState(false);

  // Da aperta la riga non è troncata per costruzione: si misura solo da
  // chiusa, e intanto il chevron resta per poterla richiudere.
  useEffect(() => {
    const box = valueRef.current;
    if (!expandable || open || !box) return;
    const check = () => {
      const v = box.querySelector('.editable-field__value');
      setTruncated(!!v && v.scrollWidth > v.clientWidth);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(box);
    return () => ro.disconnect();
  }, [expandable, open, value]);

  const [valueNode, trailing] = children;
  return (
    <div className={`krow ${open ? 'krow--open' : ''}`}>
      <span>{label}</span>
      <span className="krow-v" ref={valueRef}>{valueNode}</span>
      {(truncated || open) && (
        <IconButton
          size="small"
          className="krow-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={t(open ? 'common.collapse' : 'common.expand')}
          title={t(open ? 'common.collapse' : 'common.expand')}
        >
          <ExpandMoreIcon fontSize="inherit" />
        </IconButton>
      )}
      {trailing}
    </div>
  );
}

/**
 * Sezione "Informazioni mancanti": rende le MessageInfo con value
 * vuoto. Il value e' editabile (doppio click) anche qui: compilando
 * un campo la riga si sposta automaticamente nelle "estratte" al
 * successivo render (Redux aggiorna state.items e l'effect di
 * sincronizzazione in MessageInbox ricarica selected).
 *
 * Empty state quando tutte le chiavi sono popolate: informa positivamente.
 */
function MessageInfoMissing({
  message,
  onInfoUpdated,
}: {
  message: Message;
  onInfoUpdated: (fresh: MessageInfo) => void;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const groups = useMemo(
    () => groupsBySection(message.extractorInfos ?? [], 'missing'),
    [message.extractorInfos],
  );
  const count = groups.reduce((n, g) => n + g.infos.length, 0);
  return (
    <section className="know-col know-col--warn">
      <div className="know-h">
        <span className="know-dot warn" />
        {t('message.missingInfo')}
        <span className="know-n">{count}</span>
      </div>
      {groups.length === 0 ? (
        <p className="know-empty">{t('message.infos.allExtracted')}</p>
      ) : (
        groups.map((g) => (
          <MissingInfoGroup
            key={g.className ?? '_unknown'}
            group={g}
            onInfoUpdated={onInfoUpdated}
            dispatch={dispatch}
            toast={toast}
          />
        ))
      )}
    </section>
  );
}

/**
 * Sotto-sezione "Mancanti per extractor". Variant warning (arancio). Stesso
 * pattern di {@link GroupedInfoRows} ma con variant text (no textarea: in
 * "mancanti" tutti i value sono vuoti, l'edit comincia da empty).
 */
function MissingInfoGroup({
  group,
  onInfoUpdated,
  dispatch,
  toast,
}: {
  group: ExtractorGroup;
  onInfoUpdated: (fresh: MessageInfo) => void;
  dispatch: ReturnType<typeof useAppDispatch>;
  toast: ReturnType<typeof useToast>;
}) {
  const { t } = useTranslation();
  const groupTitle = t(`messageInfo.groups.${extractorShortName(group.className)}`);
  // Riga `.kmiss` del concept: l'etichetta di cosa manca, e il valore resta
  // compilabile inline — appena si riempie, la riga migra da sola in "Cosa
  // sappiamo" al render successivo.
  return (
    <>
      <div className="know-sub">{groupTitle}</div>
      {group.infos.map((info) => (
        <div className="kmiss" key={info.messageInfoId}>
          <AlertGlyph />
          <span>{getLocalizedLabel(info, t)}</span>
          <span className="askmini-field">
            <EditableField
              value={info.value}
              variant={{ kind: 'text' }}
              label={getLocalizedLabel(info, t)}
              onCommit={(v) => commitMessageInfoValue(dispatch, info, v, onInfoUpdated, toast, t)}
            />
          </span>
        </div>
      ))}
    </>
  );
}
